import React, { useCallback, useEffect, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { useLanguage } from '../../i18n/LanguageContext';

const labels = {
  off: 'Off',
  unconfigured: 'Owner or interval not configured',
  unverified: 'No successful monitor run verified',
  stalled: 'Monitor stalled',
  failed: 'Monitor failed',
  attention: 'Needs attention',
  healthy: 'Monitor healthy',
};
const dates = {
  lastRunAt: 'Last monitor run',
  lastSuccessAt: 'Last successful release check',
  lastErrorAt: 'Last monitor error',
  oldestBacklogAt: 'Oldest pending check',
};
const counts = {
  backlogCount: 'Orders awaiting checks',
  heldCount: 'Processing orders',
  manualReviewCount: 'Order reviews required',
  pendingDeliveryCount: 'Orders awaiting form delivery',
};
const date = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const isAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem('user'))?.role === 'admin';
  } catch {
    return false;
  }
};

export default function DistributorReleaseMonitorHealth() {
  const { t, locale } = useLanguage();
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const { begin, version } = useShippingRequest('distributor-release-monitor-health');
  const load = useCallback(async () => {
    const request = begin();
    setHealth(null);
    setError('');
    if (!isAdmin()) return;
    try {
      const value = (await distributorService.adminReleaseMonitorHealth({ signal: request.signal }))
        ?.data?.data;
      if (!request.isCurrent()) return;
      if (
        typeof value?.enabled !== 'boolean' ||
        typeof value.ready !== 'boolean' ||
        !Object.hasOwn(labels, value.status) ||
        !(
          value.owner === null ||
          (typeof value.owner === 'string' &&
            value.owner.trim().length > 0 &&
            value.owner.length <= 120)
        ) ||
        !(
          value.intervalMinutes === null ||
          (Number.isInteger(value.intervalMinutes) &&
            value.intervalMinutes >= 1 &&
            value.intervalMinutes <= 1440)
        ) ||
        !Object.keys(counts).every((key) => Number.isSafeInteger(value[key]) && value[key] >= 0) ||
        !Object.keys(dates).every((key) => value[key] === null || date(value[key])) ||
        ![null, 'RELEASE_UNVERIFIED'].includes(value.errorCode) ||
        (value.ready &&
          (!value.enabled ||
            value.status !== 'healthy' ||
            !value.owner ||
            !value.intervalMinutes ||
            !date(value.lastSuccessAt)))
      )
        throw new Error('Invalid release health');
      setHealth(value);
    } catch {
      if (request.isCurrent())
        setError('Order release health is unavailable. Readiness is unconfirmed.');
    }
  }, [begin]);
  useEffect(() => {
    load();
  }, [load, version]);
  if (!isAdmin()) return null;
  return (
    <section
      className="me-distributor-admin-card me-bank-monitor space-y-3"
      aria-label={t('Order release monitor health')}
    >
      <header className="me-bank-monitor-header">
        <h2>{t('Order release monitor health')}</h2>
        <button type="button" onClick={load} aria-label={t('Refresh order release health')}>
          {t('Refresh')}
        </button>
      </header>
      {error && <p role="alert">{t(error)}</p>}
      {!health && !error && <p role="status">{t('Loading order release health...')}</p>}
      {health && (
        <>
          <p
            role="status"
            className={`me-bank-monitor-status ${health.ready ? 'is-ready' : 'is-blocked'}`}
          >
            {t(labels[health.status])} · {t(health.ready ? 'Monitor ready' : 'Activation blocked')}
          </p>
          {Object.entries(counts)
            .filter(([key]) => health[key] > 0)
            .map(([key, label]) => (
              <p key={key}>
                {t(label)}: {health[key].toLocaleString(locale)}
              </p>
            ))}
          {health.lastSuccessAt && (
            <p>
              {t(dates.lastSuccessAt)}: {new Date(health.lastSuccessAt).toLocaleString(locale)}
            </p>
          )}
          <details className="me-bank-monitor-details">
            <summary>{t('Details')}</summary>
            <p>
              {t('Operational owner')}: {health.owner || t('Unassigned')}
            </p>
            {health.intervalMinutes !== null && (
              <p>
                {t('Monitor interval (minutes)')}: {health.intervalMinutes ?? t('Unavailable')}
              </p>
            )}
            {Object.entries(counts)
              .filter(([key]) => health[key] === 0)
              .map(([key, label]) => (
                <p key={key}>
                  {t(label)}: {health[key].toLocaleString(locale)}
                </p>
              ))}
            {Object.entries(dates)
              .filter(([key]) => health[key] && key !== 'lastSuccessAt')
              .map(([key, label]) => (
                <p key={key}>
                  {t(label)}:{' '}
                  {health[key] ? new Date(health[key]).toLocaleString(locale) : t('Unavailable')}
                </p>
              ))}
          </details>
          {health.status === 'attention' && (
            <p>
              {t(
                'Review processing orders and payment recovery below. Uncertain delivery forms require mailbox review.'
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}
